<script lang="ts" setup>
import { useRouter, useRoute } from 'vue-router'
import { DynamicForm } from '../../dynamic-form'
import { getForm, getDataTable } from '../../../core/services/forms'
import { onBeforeMount, ref } from 'vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { DLoading } from 'front-library'
import {
  getPayrollData,
  createPayroll,
  updatePayroll
} from '../../../core/services/payrolls'
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

/* store, router, notifications */
const notifications = useNotificationsStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const customForm = ref<any>({})
const dynamicForm = ref<typeof DynamicForm | null>(null)
const showForm = ref(true)
let model: any = {}

// Estado de bloqueo de guardado por validación de código
const isCodeBlocked = ref(false)

const handleBlockSave = (blocked: boolean) => {
  isCodeBlocked.value = blocked
}

const onSubmit = async (model: any) => {
  // Verificar si el código está bloqueado
  if (isCodeBlocked.value) {
    notifications.addNotification({
      title: t('error.titles.create-entity-error'),
      text: t('payrolls.validation.cannotSave'),
      type: 'error',
      time: 5000
    })
    return
  }

  loading.value = true
  let saved = false
  try {
    if (!route.params.id) {
      saved = await createPayroll(model)
    } else {
      saved = await updatePayroll(model, `${route.params.id as string}`)
    }
    if (saved) {
      loading.value = false
      notifications.addNotification({
        title: !route.params.id
          ? t('notification.created')
          : t('notification.edited'),
        text: t('payrolls.notification.action', [
          !route.params.id
            ? t('notification.wasCreated')
            : t('notification.wasEdited')
        ]),
        type: 'success',
        time: 10000
      })
      void router.push({ name: 'ListPayrolls' })
    }
  } catch (error: any) {
    notificationError(error, 'payrolls', t, notifications, loading)
  }
}

const getEntity = async () => {
  if (!route.params.id) return
  model = await getPayrollData(route.params.id as string)
  if (model === undefined) {
    notifications.addNotification({
      title: t('notification.notFound'),
      text: t('payrolls.notification.error'),
      type: 'error',
      time: 10000
    })
    back()
  }
}

const lastCompanyId = ref<string | undefined>(undefined)
const onUpdateField = async (fields: any) => {
  if (fields.name === 'companyId' && fields.value !== lastCompanyId.value) {
    loading.value = true
    showForm.value = false
    await loadForm(fields.value)
    showForm.value = true
    lastCompanyId.value = fields.value
    await new Promise(resolve => setTimeout(resolve, 500))
    dynamicForm.value?.setModelValue('companyId', fields.value)
    loading.value = false
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListPayrolls' })
}

const loadForm = async (companyId?: string) => {
  try {
    await getEntity()
    customForm.value = await getForm({ entity: 'payrolls', company: companyId })
    if (route.params.id !== undefined) {
      messageLoading.value = t('loading.data')
      // validar tipo consecutivo
      const employeeCodeTypes: any = await getDataTable(
        'employee_code_types',
        model.employeeCodeType
      )
      const employeeCode: string = employeeCodeTypes[0].code
      if (employeeCode === '1') {
        for (const tab of customForm.value.tabs) {
          for (const group of tab.groups) {
            const employeeCodeConsecutive = group.fields?.find(
              (f: any) => f.name === 'employeeCodeConsecutive'
            )
            if (employeeCodeConsecutive?.if === false) {
              employeeCodeConsecutive.if = true
            }
          }
        }
        dynamicForm.value?.setFieldsRules()
      }
    }
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('payrolls.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'payrolls', t, notifications, loading)
    }
  }
}

onBeforeMount(async () => {
  loading.value = true
  await loadForm()
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <div class="h-full px-5">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="
            route.params.id == undefined
              ? t('payrolls.create')
              : t('payrolls.edit')
          " />
      </div>
    </div>
    <DynamicForm
      ref="dynamicForm"
      v-if="showForm && customForm?.tabs"
      :form-schema="customForm"
      :initial-model="model"
      :title-btn-save="
        route.params.id === undefined
          ? t('form.button.save')
          : t('form.button.update')
      "
      @update-field="onUpdateField"
      @cancel="back"
      @submit="onSubmit"
      @block-save="handleBlockSave" />
  </div>
</template>
